import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Product } from '../../../types/product'
import { useSelector } from 'react-redux'
import { State } from '../../../types/state'

interface ProductsListProps {
  products: Product[]
  onEdit: (productId: number) => void
  onDelete: (productId: number) => void
  emptyMessage: string
  onClearFilters: () => void
}

const ProductsList: React.FC<ProductsListProps> = ({
  products,
  onEdit,
  onDelete,
  emptyMessage,
  onClearFilters
}) => {
  const navigate = useNavigate()
  const { isSuperadmin } = useSelector((state: State) => state.user)

  const handleProductClick = (productId: number) => {
    navigate(`/products/${productId}`)
  }

  const renderCategoryName = (category: string) => {
    if (!category) return null
    const parts = category.split('/')
    if (parts.length <= 1) {
      return category
    }
    return (
      <span className='category-hierarchy'>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className='category-separator'> / </span>}
            <span className={`category-part ${index === parts.length - 1 ? 'category-leaf tw-text-white' : 'category-parent'}`}>
              {part}
            </span>
          </React.Fragment>
        ))}
      </span>
    )
  }

  return (
    <div className='row'>
      {products.length > 0
        ? (
            products.map(product => (
              <div key={product.id} className='col-sm-6 col-md-4 mb-30'>
                <div
                  className='tw-relative tw-aspect-[4/3] tw-rounded-lg tw-overflow-hidden tw-cursor-pointer tw-group'
                  onClick={() => handleProductClick(product.id)}
                >
                  {isSuperadmin && (
                    <div className='tw-absolute tw-top-2 tw-right-2 tw-z-10 tw-flex tw-gap-2'>
                      <button
                        className='tw-bg-white tw-p-2 tw-rounded-full tw-shadow-md hover:tw-bg-slate-100 tw-transition-colors'
                        onClick={(e) => { e.stopPropagation(); onEdit(product.id) }}
                        title='Edytuj produkt'
                      >
                        <i className='fas fa-edit tw-text-green' />
                      </button>
                      <button
                        className='tw-bg-white tw-p-2 tw-rounded-full tw-shadow-md hover:tw-bg-slate-100 tw-transition-colors'
                        onClick={(e) => { e.stopPropagation(); onDelete(product.id) }}
                        title='Usuń produkt'
                      >
                        <i className='fas fa-trash-alt tw-text-red-500' />
                      </button>
                    </div>
                  )}
                  <div className='tw-absolute tw-inset-0'>
                    <img
                      src={product.imagePresignedUrl || product.image || '/assets/images/product-placeholder.jpg'}
                      alt={product.name}
                      className='tw-w-full tw-h-full tw-object-cover'
                      onError={(e) => {
                        e.currentTarget.src = '/assets/images/product-placeholder.jpg'
                      }}
                    />
                  </div>
                  <div className='tw-absolute tw-inset-0 tw-bg-black/70 tw-flex tw-items-center tw-justify-center tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-100'>
                    <div className='tw-text-center tw-p-4'>
                      <h3 className='tw-text-white tw-text-lg tw-font-semibold tw-mb-2'>
                        {product.name}
                      </h3>
                      <p className='tw-text-white tw-text-sm tw-mb-1'>
                        Marka: {product.brand?.name}
                      </p>
                      <p className='tw-text-white tw-text-sm tw-mb-2'>
                        {product.category && (
                          <span className='tw-text-white'>
                            {renderCategoryName(product.category)}
                          </span>
                        )}
                      </p>
                      <div className='tw-flex tw-flex-wrap tw-justify-center tw-gap-1'>
                        {product.fileFormats.map((format, index) => (
                          <span
                            key={index}
                            className='tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-bg-white/20 tw-text-white'
                          >
                            {format}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )
        : (
          <div className='col-12 text-center py-5'>
            <h4>{emptyMessage}</h4>
            <p>Spróbuj zmienić parametry wyszukiwania lub dodaj nowy produkt.</p>
            <button
              className={`
                                      tw-mt-3 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                    `}
              onClick={onClearFilters}
            >
              Wyczyść filtry
            </button>
          </div>
          )}
    </div>
  )
}

export default ProductsList
